import React, {
  useContext,
  useState,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useTransition
} from 'react';
import {
  TicketClient,
  RMMClient,
  AccountClient,
  startTransition,
  useCustomTransition
} from '@nile/graphql';
import styled from 'styled-components';
import HomePage from '@nile/mangifera/src/Pages/HomePage';
import { HomeContext, TicketContext } from '@nile/mangifera/src/Contexts';
import { TicketContextProvider } from '@nile/mangifera/src/Contexts/TicketContext';
import { Close } from '@styled-icons/evaicons-solid';
import {
  AppContext,
  EventEmitter,
  actionToast,
  useStateCallback,
  getCurrentUserHook,
  Loader,
  useActivityTracker,
  useCheckFeatureGating,
  useUserMSPCheckHook,
  toast,
  RefreshIcon,
  ALL_MODULES
} from '@nile/components';
import { AppProvider } from '@shopify/polaris';
import Tabs from '@nile/mangifera/src/Containers/TabsList';
import { PlusCircle } from '@styled-icons/boxicons-solid';
import { cloneDeep, pick, includes, isEqual, isEmpty } from 'lodash';
import { Redirect, withRouter } from 'react-router-dom';
import shortid from 'shortid';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorPage from '@nile/components/src/Components/ErrorPage';
import { FormattedMessage } from 'react-intl';
import envConfig from '@nile/home/src/Config/env';
import {
  getTabId,
  getTabUrl,
  getTabDisplayData,
  getTabComponent,
  getTabUrlNew,
  getServiceName
} from '@nile/mangifera/src/Pebbles/TabManager';
import AppInfoNavigation from '@nile/home/src/AppInfoNavigation';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { clearState } from '@nile/components/src/Managers/StateManager';
import { SVGIcon } from '@superops/super-icons';
import { getTicketUpdates } from '@nile/mangifera/src/Utils/Hooks';
import { getInvoiceUpdates } from '@nile/client_management/src/Utils/Hooks';
import { getState } from '@nile/components/src/Managers/StateManager';
import { Modal, TextContainer } from '@superops/ui-design-system';
import OnboardingGuide from './OnboardingGuide';
import OnBoardingPersonalisationHook from '@nile/mangifera/src/Pebbles/onBoardingPersonalisationHook.js';
import { getTabPersonalisationCategory } from '@nile/components/src/Utils/helpers';
import { useTabStore } from '@nile/mangifera/src/StoreManager/store/tabStore';
import LDDebugger from '@nile/components/src/Components/LDDebug';
import { loggerInfo } from '@nile/home/src/coralogixRUM';
import { useGlobalChatHook } from '@nile/components/src/Components/GlobalChatHook';

const StyledTabsContainer = styled.div`
  filter: ${(props) => (props.overlay ? 'blur(5px)' : 'none')};
  height: 100%;
  overflow: hidden;
  .module-logo {
    height: 25px;
    width: 25px;
  }
`;

/**
 * Fallback component to render if the error is caught.
 */
function ErrorFallback({ error, resetErrorBoundary }) {
  return (
    <ErrorPage
      imageURL={`url(${envConfig.imageRoot}svg/PageNotFound.svg)`}
      text={<FormattedMessage id="app.error.somethingWentWrong" />}
      description={
        <FormattedMessage id="app.error.somethingWentWrong.description" />
      }
    />
  );
}

/**
 * To expose the methods of tab manager, we need to forwardRef the component.
 * @param {ReactElement} Component
 */

const withRouterForwardRef = (Component) => {
  const WithRouter = withRouter(({ forwardedRef, ...props }) => (
    <Component ref={forwardedRef} {...props} />
  ));

  return forwardRef((props, ref) => (
    <WithRouter {...props} forwardedRef={ref} />
  ));
};

/**
 * To avoid closure problems while getting state values, we use this method to
 * create a reference using useRef and modify it. This is suggested by React community.
 *
 * @param {*} value
 * @param {*} isProp
 */
function useAsyncReference(value, isProp = false) {
  const ref = useRef(value);
  const [, forceRender] = useState(false);
  function updateState(newState) {
    if (!Object.is(ref.current, newState)) {
      ref.current = newState;
      forceRender((s) => !s);
    }
  }
  if (isProp) {
    ref.current = value;
    return ref;
  }
  return [ref, updateState];
}

const TabManagerFC = forwardRef((props, ref) => {
  const appContext = useContext(AppContext);
  const homeContext = useContext(HomeContext);
  const { getGlobalConversations } = useGlobalChatHook();

  const flags = useFlags();
  const getAllTabProperties = () => {
    if (homeContext?.userAppState?.appState) {
      return homeContext.userAppState.appState.map((el) => {
        return {
          ...el
        };
      });
    }
    return [];
  };
  const {
    tabs: storeTabs,
    setTabs,
    activeTabId,
    setActiveTab,
    removeStoreForTab,
    removeAllTabs,
    removeTab
  } = useTabStore();
  const errorStatuses = ['BOUNCED', 'DROPPED', 'BLOCKED'];
  const isShiftKeyPressedRef = useRef();
  const [errors, setErrors] = useState(true);
  const [lastUpdatedTime, setLastUpdatedTime] = useState('now');
  const [serviceUnavailable, setServiceUnavailable] = useState(null);
  const allTabsList = getAllTabProperties();
  const correctedTabIndex =
    homeContext?.userAppState?.activeTabIndex >= allTabsList?.length
      ? 0
      : homeContext?.userAppState?.activeTabIndex;
  const [state, setState] = useAsyncReference({
    tabs: getAllTabProperties(),
    selected: correctedTabIndex,
    enablePersonalization: true,
    globalState: {},
    activeTab:
      correctedTabIndex > -1
        ? homeContext?.userAppState?.appState?.[correctedTabIndex]
        : {}
  });

  const [isPending, startTransition] = useTransition();
  const [notifyRefreshTab, setNotifyRefreshTab] = useStateCallback(false);
  const { actions } = props;

  const { sendClientEvent } = getTicketUpdates(
    state?.current?.activeTab?.data?.ticketId
  );
  const { sendClientEvent: sendInvoiceEvent } = getInvoiceUpdates(
    state?.current?.activeTab?.data?.invoiceId
  );
  broadcastChannel.onmessage = (event) => {
    if (flags?.ticketStatus && event.data) {
      const eventData = JSON.parse(event.data);
      const { context, payload } = eventData;
      const conversation = payload?.data?.conversation;
      if (
        context === 'pageUpdate' &&
        payload?.type === 'SERVER_EVENT' &&
        payload?.event === 'UPDATE_CONVERSATION' &&
        eventData?.showToast &&
        (conversation?.outgoingMailStatus === 'FAILURE' ||
          errorStatuses.includes(conversation?.mailDeliveryStatus))
      ) {
        const isTicketSideConversation =
          conversation?.workItem?.module === 'TICKET';
        const failureReason = JSON.parse(
          conversation?.outgoingMailFailureReason ||
            conversation?.mailDeliveryMessage
        );
        if (isTicketSideConversation) {
          actionToast(
            'error',
            `app.errorCode.${failureReason?.errorCode}`,
            {
              title: `Ticket ${conversation?.workItem?.displayId}`,
              action: () => {
                openTab({
                  ticketId: conversation?.workItem?.workId,
                  module: conversation?.workItem?.module,
                  data: {
                    displayId: conversation?.workItem?.displayId,
                    ticketId: conversation?.workItem?.workId
                  }
                });
              }
            },
            'en',
            {
              autoClose: false,
              toastId: `Sops-error-toast-${conversation?.workItem?.workId}`
            },
            {
              errorOccurredEmailId: conversation?.emailAddress
            }
          );
        } else {
          toast('error', `app.errorCode.${failureReason?.errorCode}`);
        }
      }
    }
  };

  const checkGlobalTicketConversation = (ticketId) => {
    const globalTicketConversation = getGlobalConversations() || [];

    const activeGlobalChat = globalTicketConversation.find(
      (conversation) => conversation?.id === ticketId
    );

    return activeGlobalChat;
  };

  const unregiserAllTicketPage = (e) => {
    // e.preventDefault();
    // e.returnValue = true;
    const tickets = state.current?.tabs?.filter((tab) => {
      return (
        tab?.module === 'TICKET' &&
        tab?.data?.ticketId &&
        tab?.id !== state.current?.activeTab?.id
      );
    });
    if (tickets.length) {
      tickets.forEach((ticket) => {
        if (ticket?.data?.ticketId && sendClientEvent)
          sendClientEvent('UNREGISTER_PAGE', ticket?.data?.ticketId);
      });
    }
  };

  const unregiserAllInvoicePage = () => {
    // e.preventDefault();
    // e.returnValue = true;

    const invoices = state.current?.tabs?.filter((tab) => {
      return (
        tab?.module === 'INVOICE' &&
        tab?.data?.invoiceId &&
        tab?.id !== state.current?.activeTab?.id
      );
    });
    if (invoices.length) {
      invoices.forEach((invoice) => {
        if (invoice?.data?.invoiceId && sendInvoiceEvent)
          sendInvoiceEvent('UNREGISTER_PAGE', invoice?.data?.invoiceId);
      });
    }
  };

  const toggleRegisterTicketPage = (e) => {
    const isGlobalTicketConverationPresent = checkGlobalTicketConversation(
      state.current?.activeTab?.data?.ticketId
    );

    if (document.visibilityState === 'visible') {
      if (state.current?.activeTab?.data?.ticketId && sendClientEvent) {
        sendClientEvent(
          'REGISTER_PAGE',
          state.current?.activeTab?.data?.ticketId
        );
      }
      if (state.current?.activeTab?.data?.invoiceId && sendInvoiceEvent) {
        sendInvoiceEvent(
          'REGISTER_PAGE',
          state.current?.activeTab?.data?.invoiceId
        );
      }
    } else {
      if (
        state.current?.activeTab?.data?.ticketId &&
        sendClientEvent &&
        !isGlobalTicketConverationPresent
      ) {
        sendClientEvent('EXIT_PAGE', state.current?.activeTab?.data?.ticketId);
      }
      if (state.current?.activeTab?.data?.invoiceId && sendInvoiceEvent) {
        sendInvoiceEvent(
          'EXIT_PAGE',
          state.current?.activeTab?.data?.invoiceId
        );
      }
    }
  };

  useEffect(() => {
    const toggleMetaKey = (e) => {
      isShiftKeyPressedRef.current = e.shiftKey && !e.metaKey && !e.ctrlKey;
    };

    function resetShiftPress() {
      isShiftKeyPressedRef.current = false;
    }

    EventEmitter.subscribe('notifyRefresh', notifyRefresh);
    EventEmitter.subscribe('serviceUnavailableError', handleServiceUnavailable);

    window.addEventListener('beforeunload', unregiserAllTicketPage);
    window.addEventListener('beforeunload', unregiserAllInvoicePage);
    window.addEventListener('keydown', toggleMetaKey);
    window.addEventListener('keyup', toggleMetaKey);
    document.addEventListener('visibilitychange', toggleRegisterTicketPage);
    window.addEventListener('blur', resetShiftPress);

    return () => {
      console.log('pvs unmount');
      EventEmitter.unsubscribe('notifyRefresh');
      EventEmitter.unsubscribe('serviceUnavailableError');
      window.removeEventListener('beforeunload', unregiserAllTicketPage);
      window.removeEventListener('beforeunload', unregiserAllInvoicePage);
      document.removeEventListener(
        'visibilitychange',
        toggleRegisterTicketPage
      );
      window.removeEventListener('keydown', toggleMetaKey);
      window.removeEventListener('keyup', toggleMetaKey);
      window.removeEventListener('blur', resetShiftPress);
    };
  }, []);

  /**
   * Initially when the app loads, we get the user personalised value from backend and using that
   * we calculate the tab properties and set it to the state
   */
  useEffect(() => {
    const _tabs = getAllTabProperties() || [];
    setState({
      ...state.current,
      tabs: _tabs
    });
    setTabs(_tabs);
  }, [homeContext?.userAppState]);

  // Strip any cached React elements from existing tabs to avoid retaining component trees
  useEffect(() => {
    const currentState = getCurrentState();
    const existingTabs = currentState?.tabs || [];
    try {
      if (existingTabs.some((t) => t && t.component)) {
        const sanitizedTabs = existingTabs.map((t) => {
          if (t && t.component) {
            const nt = { ...t };
            delete nt.component;
            if (nt.content) {
              delete nt.content;
            }
            return nt;
          }
          return t;
        });
        setState({
          ...currentState,
          tabs: sanitizedTabs
        });
        setTabs(sanitizedTabs);
      }
    } catch (error) {
      console.error('Error in removing component during sanity', error);
    }
  }, []);

  useEffect(() => {
    setState({
      ...state.current,
      selected:
        correctedTabIndex > -1 ? correctedTabIndex : state.current.selected
    });
  }, [homeContext?.userAppState?.activeTabIndex]);

  const navigateToNotFound = () => {
    openTab({
      ticketId: 'Not found',
      module: 'TICKET',
      data: {
        displayId: 'Not found',
        ticketId: 'Not found'
      }
    });
  };

  const getTabModule = (url, allUrl) => {
    function generateRegExpPattern(dynamicPath) {
      const escapedDynamicPath = dynamicPath.replace(
        /[.*+?^${}()|[\]\\]/g,
        '\\$&'
      );
      const pattern = escapedDynamicPath.replace(/:\w+/g, '[^/]+');
      const regex = new RegExp(`^${pattern}$`);
      return regex;
    }

    let moduleName;
    const data = {};
    let pathElem;
    let pathElemCount = 0;

    const urlParams = url.split('/');
    urlParams.splice(0, 1);

    if (
      (urlParams?.[0] === 'ticket' || urlParams?.[0] === 'tasks') &&
      !Number.isNaN(Number(urlParams?.[1]))
    ) {
      return { module: 'WORKSPACE', data: data };
    }

    for (let key in allUrl) {
      let urlRegex;
      if (Array.isArray(allUrl[key].path)) {
        allUrl[key].path.forEach((elem, idx) => {
          urlRegex = generateRegExpPattern(elem);
          if (urlRegex.test(url)) {
            if (pathElemCount < 1) {
              moduleName = key;
              pathElem = elem;
              pathElemCount = 1;
            }
          }
        });
      } else {
        urlRegex = generateRegExpPattern(allUrl[key].path);
        if (urlRegex.test(url)) {
          if (pathElemCount < 1) {
            moduleName = key;
            pathElem = allUrl[key].path;
            pathElemCount = 1;
          }
        }
      }
    }

    if (!pathElem) {
      return { module: 'NOTFOUND', data: data };
    }
    const path = pathElem.split('/');
    const values = url.split('/');
    // console.log(path, values);

    for (let i = 0; i < path.length; i++) {
      if (path[i].startsWith(':')) {
        data[path[i].substring(1)] = values[i];
      }
    }

    return { module: moduleName, data: data };
  };

  const navigateToURL = (url, urlParams, queryParams) => {
    const params = {
      url,
      urlParams,
      data: {
        ticketId: urlParams[1],
        id: urlParams?.[3]
      },
      queryParams
    };
    const allUrl = getTabUrlNew({ ...params, getAllURL: true });
    const moduleData = getTabModule(url, allUrl);
    const { module } = moduleData;
    params.data = moduleData.data;

    if (!module) {
      navigateToNotFound();
    }

    openTab({
      ...params,
      module
    });
  };

  const navigateToTimesheet = (urlParams) => {
    if (urlParams?.[0] === 'timesheet') {
      openTab({
        ticketId: urlParams[1],
        module: 'TIMESHEET'
      });
    } else {
      navigateToNotFound();
    }
  };

  const navigateToTicketDetails = (urlParams) => {
    if (urlParams?.[0] === 'tickets' && urlParams?.[2] === 'ticket') {
      openTab({
        ticketId: urlParams[1],
        module: 'TICKET',
        data: {
          displayId: urlParams[1],
          ticketId: urlParams[1]
        }
      });
    } else {
      navigateToNotFound();
    }
  };

  const navigateToSharedReport = (urlParams) => {
    if (urlParams?.[0] === 'sharedreport' && urlParams?.[2] === 'view') {
      openTab({
        reportId: urlParams[1],
        module: 'SHARED_REPORT',
        data: {
          title: urlParams[1],
          reportId: urlParams[1]
        }
      });
    } else {
      navigateToNotFound();
    }
  };

  const navigateToClient = (urlParams, url, queryParams) => {
    if (urlParams?.[0] === 'client' && !Number.isNaN(Number(urlParams?.[1]))) {
      const currentState = getCurrentState();
      const { tabs, selected, activeTab } = currentState;
      const existingTabProperties = tabs.find(
        (el) => el.id === `client-${urlParams[1]}`
      );
      openTab({
        module: 'CLIENT',
        url: url,
        queryParams: queryParams,
        data: {
          id: urlParams[1],
          name: existingTabProperties?.data?.name || 'Client',
          value: urlParams[2]
        },
        ...(existingTabProperties?.state && {
          state: existingTabProperties?.state
        })
      });
    }
  };

  const handleServiceUnavailable = (args) => {
    const currentState = getCurrentState();
    const { tabs, selected, activeTab } = currentState;
    const currentServiceName = getServiceName({ module: activeTab.module });
    if (args.serviceName === currentServiceName) {
      setServiceUnavailable(true);
    }
  };

  const navigateToTaskWorkspace = (urlParams, url, queryParams) => {
    const currentState = getCurrentState();
    const { tabs, selected, activeTab } = currentState;
    if (activeTab.id !== 'workspace') {
      if (urlParams?.[0] === 'tasks' && !Number.isNaN(Number(urlParams?.[1]))) {
        openTab({
          module: 'WORKSPACE',
          url: url,
          queryParams: queryParams
        });
      } else {
        navigateToNotFound();
      }
    }
  };

  const navigateToInvoice = (urlParams) => {
    if (
      urlParams?.[0] === 'invoiceList' &&
      !Number.isNaN(Number(urlParams?.[1]))
    ) {
      openTab({
        module: 'INVOICE',
        data: { invoiceId: urlParams?.[1] }
      });
    } else {
      navigateToNotFound();
    }
  };

  const navigateToQuote = (urlParams) => {
    if (
      urlParams?.[0] === 'quoteList' &&
      !Number.isNaN(Number(urlParams?.[1]))
    ) {
      openTab({
        module: 'QUOTE',
        data: { quoteId: urlParams?.[1] }
      });
    } else {
      navigateToNotFound();
    }
  };

  const navigateToAsset = (urlParams) => {
    if (
      urlParams?.[0] === 'rmm' &&
      urlParams?.[1] === 'asset' &&
      !Number.isNaN(Number(urlParams?.[3]))
    ) {
      openTab({
        module: 'ASSET_DETAILS',
        data: { id: urlParams?.[3] }
      });
    } else {
      navigateToNotFound();
    }
  };

  const navigateToWorkspace = (urlParams) => {
    const currentState = getCurrentState();
    const { tabs, selected, activeTab } = currentState;
    if (activeTab.id !== 'workspace') {
      if (
        urlParams?.[0] === 'ticket' &&
        !Number.isNaN(Number(urlParams?.[1]))
      ) {
        openTab({
          module: 'WORKSPACE',
          url: `/${urlParams?.[0]}/${urlParams?.[1]}/${urlParams?.[2]}`
        });
      } else {
        navigateToNotFound();
      }
    }
  };

  const navigateToInvoiceViews = (urlParams) => {
    const currentState = getCurrentState();
    const { tabs, selected, activeTab } = currentState;
    if (activeTab.id !== 'clients') {
      if (
        urlParams?.[0] === 'invoiceViews' &&
        !Number.isNaN(Number(urlParams?.[1]))
      ) {
        openTab({
          module: 'ALL_CLIENTS',
          url: `/${urlParams?.[0]}/${urlParams?.[1]}/${urlParams?.[2]}`
        });
      } else {
        navigateToNotFound();
      }
    }
  };

  const navigateToOnboard = (urlParams) => {
    const currentState = getCurrentState();
    const { tabs, selected, activeTab } = currentState;
    if (activeTab.id !== 'onboard-tab') {
      if (urlParams?.[0] === 'onboard') {
        openTab({
          module: 'ONBOARD',
          url: `/${urlParams.join('/')}`
        });
      } else {
        navigateToNotFound();
      }
    }
  };

  const navigateToReport = (urlParams) => {
    const currentState = getCurrentState();
    const { tabs, selected, activeTab } = currentState;
    if (activeTab.id !== 'reports-charts') {
      if (urlParams?.[0] === 'report' || urlParams?.[0] === 'charts') {
        openTab({
          module: 'REPORTS_CHARTS_HOME',
          url: `/${urlParams?.[0]}/${urlParams?.[1]}/${urlParams?.[2]}`
        });
      } else {
        navigateToNotFound();
      }
    }
  };

  const navigateToHome = (urlParams) => {
    const currentState = getCurrentState();
    const { tabs, selected, activeTab } = currentState;
    if (activeTab.id !== 'home-tab') {
      if (urlParams?.[0] === 'home') {
        openTab({
          module: 'HOME',
          url: `/${urlParams?.[0]}`
        });
      } else {
        navigateToNotFound();
      }
    }
  };

  const navigateToITDoc = (urlParams) => {
    const currentState = getCurrentState();
    const { tabs, selected, activeTab } = currentState;
    if (urlParams?.[0] === 'rmm' && urlParams?.[1] === 'non-monitored-asset') {
      openTab({
        module: 'NON_MONITORED_ASSET_DETAILS',
        data: {
          entityName: urlParams[3],
          classId: urlParams[4],
          assetId: urlParams[5]
        }
      });
    } else {
      navigateToNotFound();
    }
  };

  const navigateToProductOnboard = (urlParams) => {
    const currentState = getCurrentState();
    const { tabs, selected, activeTab } = currentState;
    if (urlParams?.[0] === 'productonboard') {
      openTab({
        module: 'PRODUCTONBOARD',
        url: `/${urlParams?.[0]}`
      });
    } else {
      navigateToNotFound();
    }
  };

  const navigateToMigrateData = (urlParams) => {
    const currentState = getCurrentState();
    const { tabs, selected, activeTab } = currentState;
    if (urlParams?.[0] === 'migrate') {
      openTab({
        module: 'MIGRATE_DATA',
        url: `/${urlParams?.[0]}`
      });
    } else {
      navigateToNotFound();
    }
  };

  /**
   * When we click a url, it should be redirected to our application
   * Since our application has multiple tabs, we need to identify the tab and switch focus to it.
   * This method is used when the tab is not opened in our application, but the user clicks on a link.
   * We create a new tab based on the properties.
   */

  const appNavigatorNew = () => {
    // const currentState = getCurrentState();
    // const { tabs, selected, activeTab } = currentState;
    const { url, params } = props.match;
    const queryParams = props.location.search;
    const urlParams = url.split('/');
    urlParams.splice(0, 1);
    navigateToURL(url, urlParams, queryParams);
  };

  const appNavigator = () => {
    const currentState = getCurrentState();
    const { tabs, selected, activeTab } = currentState;
    const { url, params } = props.match;
    const queryParams = props.location.search;
    const urlParams = url.split('/');
    urlParams.splice(0, 1);
    switch (urlParams[0]) {
      case 'ticket':
        navigateToWorkspace(urlParams);
        break;
      case 'tickets':
        navigateToTicketDetails(urlParams);
        break;
      case 'sharedreport':
        navigateToSharedReport(urlParams);
        break;
      case 'client':
        navigateToClient(urlParams, url, queryParams);
        break;
      case 'invoiceList':
        navigateToInvoice(urlParams);
        break;
      case 'quoteList':
        navigateToQuote(urlParams);
        break;
      case 'invoiceViews':
        navigateToInvoiceViews(urlParams);
        break;
      case 'onboard':
        navigateToOnboard(urlParams);
        break;
      case 'report':
        navigateToReport(urlParams);
        break;
      case 'home':
        navigateToHome(urlParams);
        break;
      case 'rmm':
        if (urlParams[1] === 'asset') {
          navigateToAsset(urlParams);
          break;
        } else if (urlParams[1] === 'non-monitored-asset') {
          navigateToITDoc(urlParams);
          break;
        }
        break;
      case 'productonboard':
        navigateToProductOnboard(urlParams);
        break;
      case 'migrate':
        navigateToMigrateData(urlParams);
        break;
      case 'tasks':
        navigateToTaskWorkspace(urlParams, url, queryParams);
        break;
      case 'timesheet':
        navigateToTimesheet(urlParams);
        break;
      default:
        break;
    }
  };
  // TODO - Vignesh - Handle in a different way

  const isValidUrl = (currTab, newUrl) => {
    if (includes(['/allmodules', '/home', '/createNew'], newUrl)) return false;
    const currFormat = currTab.url?.split('/');
    const newFormat = newUrl.split('/');
    if (
      includes(
        ['ticket', 'project', 'tasks', 'mails', 'modules'],
        currFormat[1]
      )
    ) {
      if (
        (currFormat[1] === 'project' || currFormat[1] === 'ticket') &&
        Number.isNaN(Number(currFormat[2]))
      ) {
        return false;
      }
      if (
        (currFormat[1] === 'project' || currFormat[1] === 'ticket') &&
        Number.isNaN(Number(newFormat[2]))
      ) {
        return false;
      }
      if (currFormat[1] === 'modules' && currFormat[3] === 'clients') {
        if (
          includes(
            [
              'mailBoxViews',
              'clientViews',
              'contractViews',
              'invoiceAuditViews',
              'invoiceViews'
            ],
            newFormat[1]
          )
        ) {
          return true;
        }
        return false;
      }
      if (includes(['ticket', 'project', 'tasks', 'mails'], newFormat[1])) {
        return true;
      }
      return false;
    }

    if (includes(['rmm'], currFormat[1])) {
      if (
        Number.isNaN(Number(currFormat[3])) !==
        Number.isNaN(Number(newFormat[3]))
      ) {
        return false;
      }
      if (currFormat.includes('scripts-library')) {
        return [
          'community',
          'scripts',
          'favourite',
          'popular',
          'interested'
        ].includes(newFormat[3]);
      }
    }

    if (
      includes(
        [
          'mailBoxViews',
          'clientViews',
          'contractViews',
          'invoiceAuditViews',
          'invoiceViews'
        ],
        currFormat[1]
      )
    ) {
      if (
        includes(
          [
            'mailBoxViews',
            'clientViews',
            'contractViews',
            'invoiceAuditViews',
            'invoiceViews'
          ],
          newFormat[1]
        )
      ) {
        return true;
      }
    } else if (
      includes(['client'], currFormat[1]) &&
      Number.isNaN(Number(newFormat[2]))
    ) {
      return false;
    } else if (currFormat[1] === newFormat[1]) return true;
    return false;
  };

  /**
   * If we click an external link, we open our app and switch focus to the tab which is mapped to the clicked URL.
   * If it's not there, we create a new tab.
   */
  useEffect(() => {
    if (flags?.urlRedirectionForTabs) {
      const currentState = getCurrentState();
      const { tabs, selected, activeTab } = currentState;
      const allTabs = cloneDeep(tabs);

      const allUrl = getTabUrlNew({ getAllURL: true });
      const moduleData = getTabModule(props?.location?.pathname, allUrl);
      const { module } = moduleData;

      const moduleIndex = allTabs.findIndex((el) => el.module === module);

      const { url, params } = props.match;
      const queryParams = props.location.search;
      const urlParams = url.split('/');
      urlParams.splice(0, 1);
      if (module === 'CLIENT') {
        navigateToClient(urlParams, url, queryParams);
        return;
      }

      //4 scenarios

      //1=>  if No module in existing tabs.. new tab gets opened via appNavigatorNew.
      //2=>  if module exist and url exist but in diff tab...  focusing that tab.
      //3=>  when module exist but url doesnt match and new tab is required ..opentab via appNavigatorNew.
      //example: tickets/:ticketid/ticket detail page
      //4=>  when module exist but url doesnt match and new tab not required ? switch tabs via module and update url.
      //example: settings page urls needs to be routed within same tab.
      //if path not given as array in allUrl..the url is supposed to be opened as new tab.
      //if urls given inside array...the url is supposed to be routed withing the same opened tab.

      if (moduleIndex < 0 && urlParams[0] !== 'clientportal') {
        appNavigatorNew();
      } else if (props?.location?.pathname !== allTabs[selected]?.url) {
        const selectedIndex = allTabs.findIndex(
          (el) => el.url === props?.location?.pathname
        );
        if (selectedIndex > -1 && activeTab.module !== module) {
          setState({
            ...currentState,
            activeTab: allTabs[selectedIndex],
            selected: selectedIndex
          });
          setActiveTab(allTabs[selectedIndex]?.id);
          handleTabRedirection(allTabs[selectedIndex], selected);
        } else if (
          !Array.isArray(allUrl[module]?.path) &&
          urlParams[0] !== 'clientportal'
        ) {
          appNavigatorNew();
        } else {
          const selectedIndex2 = allTabs.findIndex(
            (el) => el.module === module
          );
          if (selectedIndex2 > -1 && activeTab.module !== module) {
            if (isValidUrl(allTabs[selectedIndex2], props.location.pathname)) {
              allTabs[selectedIndex2].url = props.location.pathname;

              setState({
                ...currentState,
                activeTab: allTabs[selectedIndex2],
                selected: selectedIndex2,
                tabs: allTabs
              });
              setActiveTab(allTabs[selectedIndex2]?.id);
              setTabs(allTabs);
              handleTabRedirection(allTabs[selectedIndex2], selected);
            }
          }
        }
      }
    } else {
      const currentState = getCurrentState();
      const { tabs, selected, activeTab } = currentState;
      const allTabs = cloneDeep(tabs);
      if (props?.location?.pathname !== allTabs[selected]?.url) {
        const selectedIndex = allTabs.findIndex(
          (el) => el.url === props?.location?.pathname
        );
        if (selectedIndex > -1) {
          setState({
            ...currentState,
            activeTab: allTabs[selectedIndex],
            selected: selectedIndex
          });
          setActiveTab(allTabs[selectedIndex]?.id);
          setTabs(allTabs);
          handleTabRedirection(allTabs[selectedIndex], selected);
        } else {
          appNavigator();
          // handleTabRedirection(allTabs[selected], selected);
        }
      }
    }
  }, [props.location?.pathname]);

  useEffect(() => {
    const currentAppState = homeContext?.userAppState || { appState: [] };
    homeContext.updateUserAppState({
      ...currentAppState,
      appState: state.current.tabs
    });
  }, [state.current.tabs.length]);

  const updateUserAppState = () => {
    const currentAppState = homeContext?.userAppState || { appState: [] };
    homeContext.updateUserAppState({
      ...currentAppState,
      appState: state.current.tabs
    });
  };

  const getCurrentState = () => {
    return state.current;
  };

  const {
    tabs,
    selected,
    activeTab,
    enablePersonalization
  } = getCurrentState();

  /**
   *
   * Validate if the url meets the standards and returns bool
   * @param {object} currTab
   * @param {object} newUrl
   * @public
   */

  /**
   * This is the single place where tab redirection happens.
   * Whenever tab is switched/opened/closed, we redirect it to the new tab.
   * If it has a url, the route is also changed.
   * After switching, the personalised state is updated to the backend.
   * @param {object} currentTab
   * @param {Number} prevIndex
   * @param {boolean} isClose
   */
  const handleTabRedirection = (currentTab, prevIndex, isClose) => {
    const currentState = getCurrentState();
    setServiceUnavailable(false);
    if (
      !isClose &&
      currentState.selected !== prevIndex &&
      !flags?.deferTabState
    ) {
      EventEmitter.dispatch('tabChange', { prevIndex: prevIndex });
    }
    const currentUser = appContext.user;
    const { currentServicePortal, currentProduct } = appContext;

    const updatedTabs = cloneDeep(state.current.tabs);
    // FIX to clear agentOnboarding
    let URL =
      props.history.location.search && props.location.search
        ? props.location.pathname + props.location.search
        : props.location.pathname;
    if (
      !isClose && // TODO - temp - until url validation is done in nxt sprint.
      updatedTabs[prevIndex]?.url &&
      updatedTabs[prevIndex].url !== URL &&
      !updatedTabs.some((el) => el.url === URL) &&
      isValidUrl(updatedTabs[prevIndex], URL)
    ) {
      updatedTabs[prevIndex].url = URL;
    }
    setState({
      ...state.current,
      tabs: updatedTabs
    });
    updatedTabs[
      state.current?.selected < 0 ? 0 : state.current?.selected
    ].lastAccessedAt = Date.now();
    setTabs(updatedTabs);
    const personalization = {
      user: {
        firstName: currentUser.firstName,
        userId: currentUser.userId,
        email: currentUser.email,
        lastName: currentUser.lastName
      },
      category: getTabPersonalisationCategory(
        currentServicePortal,
        currentProduct
      ),
      value: JSON.stringify({
        appState: updatedTabs.map((el, idx) => {
          const base = pick(
            el,
            'data',
            'id',
            'module',
            'tabId',
            'url',
            'displayData',
            'ticketId',
            'scriptId',
            'lastAccessedAt'
          );
          return base;
        }),
        activeTabIndex:
          state.current?.selected < 0 ? 0 : state.current?.selected
      })
    };
    // const redirectionUrl = updatedTabs.find((t) => t.id === currentTab.id).url;
    const redirectionUrl = currentTab?.url;
    if (enablePersonalization) {
      actions.tabPersonalization({
        variables: { ...personalization, name: 'tabPersonalization' },
        input: { ...personalization, name: 'tabPersonalization' }
      });
    }
    setErrors(!errors);
    if (redirectionUrl) props.history.push(redirectionUrl);
  };

  const handleTabChange = (index) => {
    if (flags?.deferTabState) {
      const currentTabIndex = state?.current?.selected;
      const changedTabIndex = index;
      if (currentTabIndex !== changedTabIndex) {
        EventEmitter.dispatch('tabChange', { prevIndex: currentTabIndex });
      }
      startTransition(() => {
        handleTabChange1(index);
      });
    } else {
      handleTabChange1(index);
    }
  };

  const handleTabChange1 = (index) => {
    const currentState = getCurrentState();
    const currentActiveTab = currentState.activeTab;
    const isGlobalTicketConverationPresent = checkGlobalTicketConversation(
      currentActiveTab?.data?.ticketId
    );
    if (
      currentActiveTab?.module === 'TICKET' &&
      currentActiveTab?.data?.ticketId &&
      !isGlobalTicketConverationPresent &&
      sendClientEvent
    ) {
      sendClientEvent('EXIT_PAGE', currentActiveTab?.data?.ticketId);
    }
    if (
      currentActiveTab?.module === 'INVOICE' &&
      currentActiveTab?.data?.invoiceId &&
      sendInvoiceEvent
    ) {
      sendInvoiceEvent('EXIT_PAGE', currentActiveTab?.data?.invoiceId);
    }
    setState({
      ...currentState,
      activeTab: currentState.tabs[index],
      selected: index
    });
    setActiveTab(currentState.tabs[index]?.id);
    handleTabRedirection(currentState.tabs[index], currentState.selected);
  };

  const closeTabCb = (tab) => {
    closeTab(tab);
  };

  const openTabCb = (params) => {
    console.log('params on openTabCb', params);
    openTab(params);
  };

  /**
   * Method to add properties to the tab.
   * All the properties and their usage are given in pebbles of TabManager.
   * @param {object} params
   */

  const getTabProperties = (params) => {
    const { tabs, selected, activeTab } = getCurrentState();
    const allTabs = cloneDeep(tabs);
    const tab = {
      id: getTabId(params)
    };
    console.log('tab id :: ', getTabId(params));

    if (flags?.urlRedirectionForTabs) {
      tab.displayData = getTabDisplayData(params);
      tab.data = params.data;
      tab.url = getTabUrlNew(params);
      if (tab.url === '/:urlnotfound') {
        params.module = 'NOTFOUND';
      }
      tab.module = params.module;
    } else {
      tab.displayData = getTabDisplayData(params);
      tab.data = params.data;
      tab.url = getTabUrl(params);
      tab.module = params.module;
    }

    // Add support for URL parameters
    if (params.params && tab.url) {
      const urlParams = new URLSearchParams(params.params);
      const separator = tab.url.includes('?') ? '&' : '?';
      tab.url = `${tab.url}${separator}${urlParams.toString()}`;
    }

    if (params.state) {
      tab.state = params.state;
    }

    return tab;
  };

  /**
   * This method is used to open a new tab.
   * It's exposed to the components consuming the tabManager context
   * @param {object} params
   */

  const openTab = (params) => {
    const { tabs, selected, activeTab } = getCurrentState();
    const tab = getTabProperties(params);
    const allTabs = cloneDeep(tabs);
    // Ensure no cached component elements are retained
    allTabs.forEach((t) => {
      if (t && t.component) {
        delete t.component;
      }
      if (t && t.content) {
        delete t.content;
      }
    });
    const duplicateIndex = allTabs.findIndex((el) => el.id === tab.id);
    if (duplicateIndex > -1) {
      if (allTabs[duplicateIndex].url !== tab.url) {
        allTabs[duplicateIndex] = tab;
      }
      if (!isEqual(allTabs[duplicateIndex].state, tab.state)) {
        allTabs[duplicateIndex].state = tab.state || null;
      }

      if (
        tab?.data?.updateData &&
        !isEqual(allTabs[duplicateIndex].data, tab.data)
      ) {
        allTabs[duplicateIndex].data = tab.data || null;
      }
      if (isShiftKeyPressedRef.current) {
        if (duplicateIndex < selected) {
          allTabs.splice(selected, 0, allTabs.splice(duplicateIndex, 1)[0]);
          setState({
            ...state.current,
            tabs: allTabs,
            activeTab: allTabs[selected - 1],
            selected: selected - 1
          });
          setActiveTab(allTabs[selected - 1]?.id);
          setTabs(allTabs);
        } else {
          allTabs.splice(selected + 1, 0, allTabs.splice(duplicateIndex, 1)[0]);
          setState({
            ...state.current,
            tabs: allTabs
          });
          setActiveTab(allTabs[selected]?.id);
          setTabs(allTabs);
        }
      } else {
        setState({
          ...state.current,
          tabs: allTabs,
          activeTab: allTabs[duplicateIndex],
          selected: duplicateIndex
        });
        setActiveTab(allTabs[duplicateIndex]?.id);
        setTabs(allTabs);
        if (flags?.deferTabState && selected !== duplicateIndex) {
          EventEmitter.dispatch('tabChange', { prevIndex: selected });
          startTransition(() => {
            handleTabRedirection(allTabs[duplicateIndex], selected);
          });
        } else {
          handleTabRedirection(allTabs[duplicateIndex], selected);
        }
      }
    } else {
      allTabs.splice(selected + 1, 0, tab);
      if (isShiftKeyPressedRef.current) {
        setState({
          ...state.current,
          tabs: allTabs
        });
        setTabs(allTabs);
      } else {
        const index = selected + 1;
        setState({
          ...state.current,
          tabs: allTabs,
          activeTab: allTabs[index],
          selected: index
        });
        setActiveTab(allTabs[index]?.id);
        setTabs(allTabs);
        if (flags?.deferTabState && selected !== index) {
          EventEmitter.dispatch('tabChange', { prevIndex: selected });
          startTransition(() => {
            handleTabRedirection(allTabs[index], selected);
          });
        } else {
          handleTabRedirection(allTabs[index], selected);
        }
      }
    }
  };

  /**
   * This method is used to update the current tab.
   * It's exposed to the components consuming the tabManager context
   * @param {object} params
   */

  const updateTab = (params, tabId) => {
    // TODO: Save personalization on update
    const { tabs, selected } = getCurrentState();
    let tabIndex = tabs.findIndex((item) => {
      return item.id === tabId;
    });
    if (!tabId) {
      tabIndex = selected;
    }
    const allTabs = cloneDeep(tabs);
    const updatedTab = getTabProperties(params);
    // Ensure no cached component elements are retained
    allTabs.forEach((t) => {
      if (t && t.component) {
        delete t.component;
      }
      if (t && t.content) {
        delete t.content;
      }
    });
    allTabs[tabIndex] = updatedTab;
    setState({
      ...state.current,
      activeTab: tabId ? tabs[selected] : updatedTab,
      tabs: allTabs
    });
    setActiveTab(tabId ? tabs[selected]?.id : updatedTab?.id);
    setTabs(allTabs);
  };

  /**
   * Components should subscribe to this method if they want to store the state in the tab.
   * On tab switch, an event is emitted and the method will be called.
   * @param {object} params
   * @param {object} eventData
   */
  const updateTabState = (params, eventData) => {
    const { tabs, selected, activeTab } = getCurrentState();
    const { prevIndex } = eventData;
    const allTabs = cloneDeep(tabs);
    const currentTab = allTabs[prevIndex];
    if (currentTab) {
      currentTab.state = params;
      allTabs[prevIndex] = currentTab;
      setState({
        ...state.current,
        tabs: allTabs
      });
      setTabs(allTabs);
    }
  };

  /**
   * This method is used to reset the current tab state.
   * It's exposed to the components consuming the tabManager context
   */
  const resetTabState = () => {
    const { tabs, selected, activeTab } = getCurrentState();
    const allTabs = cloneDeep(tabs);
    const currentTab = allTabs[selected];
    currentTab.state = {};
    setState({
      ...state.current,
      tabs: allTabs,
      activeTab: currentTab
    });
    setTabs(allTabs);
  };

  /**
   *
   * @param {*} tab
   * @returns
   */

  const updateGlobalState = (properties, tabId) => {
    // don't update if the tab is removed and the tab itself is not there.
    const { globalState, tabs } = getCurrentState();
    const tabToUpdate = tabs.find((el) => el.id === tabId);
    if (tabToUpdate) {
      setState({
        ...state.current,
        globalState: {
          ...globalState,
          ...properties
        }
      });
    }
  };

  const updateGlobalStateCustom = (properties) => {
    const { globalState } = getCurrentState();
    setState({
      ...state.current,
      globalState: {
        ...globalState,
        ...properties
      }
    });
  };

  /**
   * This method is used to close the tab which is paased as argument.
   * If no argument is paased, current tab will be closed.
   * It's exposed to the components consuming the tabManager context
   * @param {object} tab
   */

  const [
    showGlobalTicketConversationWarning,
    setShowGlobalTicketConversationWarning
  ] = useState(false);

  const closeTab = (tab, closeTicketTabAndGlobalConversation) => {
    const { tabs, selected, activeTab, globalState } = getCurrentState();

    // uncomment close warning modal when user is closing the tab
    // if (
    //   tab?.module === ALL_MODULES.projectCreation &&
    //   tab?.data?.ticketId &&
    //   tab?.data?.ticketId === activeTab?.data?.ticketId &&
    //   activeTab?.module === ALL_MODULES.projectCreation
    // ) {
    //   EventEmitter.dispatch('closeProjectCreationTab', tab?.data);
    //   return;
    // }

    // // if active tab is another, close tab parameter tab is project creation tab, navigate to the project creation tab
    // if (
    //   tab?.module === ALL_MODULES.projectCreation &&
    //   tab?.id !== 'create-project'
    // ) {
    //   openTab({
    //     module: ALL_MODULES.projectCreation,
    //     data: tab?.data,
    //     url: tab?.url
    //   });
    //   return;
    // }

    const currentState = getCurrentState();
    const allTabs = cloneDeep(tabs);
    if (allTabs.length === 1) return; // temp
    let tabIndex = allTabs.findIndex((item) => {
      return item.id === tab?.id;
    });
    if (!tab) {
      tabIndex = selected;
    }
    if (tab && tabIndex === -1) {
      return;
    }

    const isTicketIdInGlobalConversation = checkGlobalTicketConversation(
      tab?.data?.ticketId
    );

    if (
      tab?.module === 'TICKET' &&
      isTicketIdInGlobalConversation &&
      !closeTicketTabAndGlobalConversation
    ) {
      setShowGlobalTicketConversationWarning(true);
      return;
    }

    if (tab?.module === 'TICKET' && tab?.data?.ticketId) {
      sendClientEvent('UNREGISTER_PAGE', tab?.data?.ticketId);
    }
    if (tab?.module === 'INVOICE' && tab?.data?.invoiceId) {
      sendInvoiceEvent('UNREGISTER_PAGE', tab?.data?.invoiceId);
    }

    let selectedIndex = selected;
    if (tabIndex <= selected) {
      selectedIndex -= 1;
    }

    if (selectedIndex < 0) selectedIndex = 0;
    const updatedGlobalState = { ...globalState };
    if (updatedGlobalState?.[tab?.id]) {
      delete updatedGlobalState[tab?.id];
    }
    removeTab(allTabs[tabIndex]?.id, allTabs, allTabs[selectedIndex]?.id);
    allTabs.splice(tabIndex, 1);
    EventEmitter.dispatch('closeTab', allTabs[tabIndex]);
    setState({
      ...currentState,
      tabs: allTabs,
      activeTab: allTabs[selectedIndex],
      selected: selectedIndex,
      globalState: updatedGlobalState
    });
    setActiveTab(allTabs[selectedIndex]?.id);
    setTabs(allTabs);
    handleTabRedirection(allTabs[selectedIndex], selected, true);
    clearState(tab?.id || activeTab?.id);
  };

  const closeAndOpenTab = (tabToClose, tabToOpen) => {
    const { tabs, selected, activeTab, globalState } = getCurrentState();
    const currentState = getCurrentState();
    let allTabs = cloneDeep(tabs);
    let tabToCloseIndex = allTabs.findIndex((item) => {
      return item.id === tabToClose?.id;
    });
    if (!tabToClose) {
      tabToCloseIndex = selected;
    }
    let selectedIndex = selected;
    if (tabToCloseIndex <= selected) {
      selectedIndex -= 1;
    }
    if (selectedIndex < 0) selectedIndex = 0;
    const updatedGlobalState = { ...globalState };
    if (updatedGlobalState?.[tabToClose?.id]) {
      delete updatedGlobalState[tabToClose?.id];
    }
    if (tabs.length > 1) {
      const tabToBeRemoved = cloneDeep(allTabs[tabToCloseIndex]);
      allTabs.splice(tabToCloseIndex, 1);
      EventEmitter.dispatch('closeTab', allTabs[tabToCloseIndex]);

      // Check if tabToOpen already exists
      const tabToOpenIndex = allTabs.findIndex((item) => {
        return item.id === tabToOpen?.id;
      });
      const tabAlreadyExists = tabToOpenIndex !== -1;

      // If tabToOpen already exists, just activate it instead of creating a new one
      if (tabAlreadyExists) {
        removeTab(tabToBeRemoved?.id, allTabs, tabToOpen?.id);
        setState({
          ...state.current,
          tabs: allTabs,
          activeTab: allTabs[tabToOpenIndex],
          selected: tabToOpenIndex,
          globalState: updatedGlobalState
        });
        setActiveTab(allTabs[tabToOpenIndex]?.id);
        setTabs(allTabs);
        handleTabRedirection(allTabs[tabToOpenIndex], selected, true);
      } else {
        const tab = getTabProperties(tabToOpen);
        allTabs.splice(selectedIndex + 1, 0, tab);
        const index = selectedIndex + 1;
        removeTab(tabToBeRemoved?.id, allTabs, allTabs[index]?.id);
        setState({
          ...state.current,
          tabs: allTabs,
          activeTab: allTabs[index],
          selected: index,
          globalState: updatedGlobalState
        });
        setActiveTab(allTabs[index]?.id);
        setTabs(allTabs);
        handleTabRedirection(allTabs[index], selected, true);
      }
    } else {
      const tab = getTabProperties(tabToOpen);
      removeTab(allTabs[0]?.id, [tab], tab?.id);
      allTabs = [tab];
      setState({
        ...state.current,
        tabs: allTabs,
        activeTab: allTabs[0],
        selected: 0,
        globalState: updatedGlobalState
      });
      setActiveTab(allTabs[0]?.id);
      setTabs(allTabs);
      handleTabRedirection(allTabs[0], selected, true);
    }
    updateUserAppState();
  };

  const isCloseAllTabRef = React.useRef(false);

  const closeAllTabs = (override) => {
    const { activeTab, globalState } = getCurrentState();
    const currentState = getCurrentState();
    let newGlobalState;
    const globalTicketConversation = getGlobalConversations() || [];
    const isTicketInFocus = globalTicketConversation.find(
      (el) => activeTab?.data?.ticketId === el?.id
    );
    // If the ticket is in focus we won't close the global ticket conversation

    if (!isEmpty(globalTicketConversation) && !override && !isTicketInFocus) {
      // If the ticket is not in focus we'll set isCloseAllTabRef to true
      // If isCloseAllTabRef is true clicking on close from modal will trigger closeAllTabs with override and close all will be called
      isCloseAllTabRef.current = true;
      setShowGlobalTicketConversationWarning(true);
      return;
    }

    if (activeTab && !isEmpty(activeTab)) {
      // Once close all tab is called we'll isCloseAllTabRef to false
      isCloseAllTabRef.current = false;
      if (globalState?.[activeTab?.id]) {
        newGlobalState = { [activeTab?.id]: globalState[activeTab.id] };
      }

      // Get all tabs that need to be removed (all except active tab)
      // const tabsToRemove = state.current.tabs
      //   .filter(tab => tab.id !== activeTab.id)
      //   .map(tab => tab.id);

      // // Clear store data for each tab individually
      // tabsToRemove.forEach(tabId => {
      //   removeStoreForTab(tabId);
      // });

      unregiserAllTicketPage();
      unregiserAllInvoicePage();

      removeAllTabs(activeTab?.id, [{ ...activeTab }], state.current.tabs);
      setState({
        ...currentState,
        tabs: [{ ...activeTab }],
        activeTab: { ...activeTab },
        selected: 0,
        globalState: newGlobalState || {}
      });
      setActiveTab(activeTab?.id);
      setTabs([{ ...activeTab }]);
      handleTabRedirection({ ...activeTab }, 0, true);
    }
  };

  const closeTicketDetailTabAndGlobalTicketConversation = () => {
    const globalConversations = getGlobalConversations();
    const activeGlobalChat = globalConversations?.find(
      (conversation) => conversation?.module === 'TICKET'
    );
    const activeGlobalChatId = activeGlobalChat?.id;
    EventEmitter.dispatch('closeGlobalConversation', activeGlobalChatId);
    useActivityTracker({
      event: 'Cancelled the pop out without sending a reply'
    });
    const allTabs = cloneDeep(tabs);
    const ticketTab = allTabs.find(
      (tab) => tab?.data?.ticketId === activeGlobalChatId
    );
    if (isCloseAllTabRef.current) {
      closeAllTabs(true);
    } else {
      closeTab(ticketTab, true);
    }
    setShowGlobalTicketConversationWarning(false);
  };

  const newTab = () => {
    const { tabs, selected, activeTab } = getCurrentState();
    const currentState = getCurrentState();
    const allTabs = cloneDeep(tabs);
    const tab = {
      id: `new-item`
    };
    const duplicateIndex = allTabs.findIndex((el) => el.id === tab.id);
    if (duplicateIndex > -1) {
      setState({
        ...state.current,
        activeTab: allTabs[duplicateIndex],
        selected: duplicateIndex
      });
      setActiveTab(allTabs[duplicateIndex]?.id);
      handleTabRedirection(allTabs[duplicateIndex], selected);
    } else {
      tab.displayData = getTabDisplayData({ module: 'NEW' });
      if (flags?.urlRedirectionForTabs) {
        tab.url = getTabUrlNew({ module: 'NEW' });
      } else {
        tab.url = getTabUrl({ module: 'NEW' });
      }

      tab.module = 'NEW';
      allTabs.push(tab);
      const index = allTabs.length - 1;
      setState({
        ...currentState,
        tabs: allTabs,
        activeTab: allTabs[index],
        selected: index
      });
      setTabs(allTabs);
      setActiveTab(allTabs[index]?.id);
      handleTabRedirection(allTabs[index], selected);
    }
  };

  /**
   * this is the hook used to expose the methods to other components
   * from where they can call the methods directly.
   */
  useImperativeHandle(ref, () => ({
    switchActiveTab: (tab, updatedTabs) => {
      const { tabs, selected, activeTab } = getCurrentState();
      const currentState = getCurrentState();
      const allTabs = cloneDeep(updatedTabs?.length ? updatedTabs : tabs);
      const index = allTabs.findIndex((el) => el.id === tab.id);
      if (index > -1) {
        setState({
          ...currentState,
          activeTab: allTabs[index],
          selected: index
        });
        setActiveTab(allTabs[index]?.id);
        handleTabRedirection(allTabs[index], selected);
      } else {
        props?.history?.push(tab.url);
      }
    },
    closeTabCb: (tab) => {
      closeTab(tab);
    },
    openTabCb: (param) => {
      openTab(param);
    },
    getCurrentState: () => {
      return getCurrentState();
    }
  }));

  const getTabSection = (tab) => {
    // let's have this method incase we need to add any extra params for specific modules
    switch (tab?.module) {
      default:
        return getTabComponent({
          ...tab,
          openTabCallback: openTabCb,
          closeTabCallback: closeTabCb
        });
    }
  };

  /**
   * This method is called from the tab list component if any changes are done to the tab list.
   * Active tab change, reordered tabs list etc.
   * @param {Array} items
   */

  const onTabChange = (items) => {
    const currentState = getCurrentState();
    const activeTabIndex = items.findIndex(
      (el) => el.id === currentState?.activeTab?.id
    );
    const reorderProps =
      activeTabIndex !== currentState?.selected
        ? { activeTab: items[activeTabIndex], selected: activeTabIndex }
        : {};
    setState({
      ...currentState,
      tabs: items,
      ...reorderProps
    });
    setActiveTab(items[activeTabIndex]?.id);
    setTabs(items);
  };

  const refreshTab = (tab) => {
    const updatedTime = new Date();
    setLastUpdatedTime(updatedTime);
    if (
      activeTab?.module === 'TICKET' &&
      activeTab?.data?.ticketId &&
      sendClientEvent
    ) {
      sendClientEvent('UNREGISTER_PAGE', activeTab?.data?.ticketId);
    }
    if (
      activeTab?.module === 'INVOICE' &&
      activeTab?.data?.invoiceId &&
      sendInvoiceEvent
    ) {
      sendInvoiceEvent('UNREGISTER_PAGE', activeTab?.data?.invoiceId);
    }
  };

  function notifyRefresh() {
    setNotifyRefreshTab(true);
  }

  function resetRefreshTab() {
    setNotifyRefreshTab(false, refreshTab);
  }

  useEffect(() => {
    if (notifyRefreshTab) setNotifyRefreshTab(false);
  }, [activeTab?.id, selected]);

  const user = getCurrentUserHook();

  const isMSP = useUserMSPCheckHook();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const chatOnboardingChargeBeePlanIds = ['Super', 'Super-2024', 'Prime']; // Show figma onboarding for these plans
  const userPlanId = user?.chargeBeePlanId; // Current user plan id
  const showFigmaOnboardingForChat = chatOnboardingChargeBeePlanIds.includes(
    userPlanId
  ); // check if the user plan id is in the list of plans for which we show the figma onboarding
  const onBoardingFlag = flags?.figmaOnboarding;
  const isTrialUser = user?.subscriptionStatus === 'TRIAL';

  const onBoardingPersonalisationHook = OnBoardingPersonalisationHook();

  const personalisationCallBack = ({ data }) => {
    if (isEmpty(data) || !data?.chatOnboardingComplete) {
      setShowOnboarding(true);
    }
  };

  useEffect(() => {
    if (
      !isEmpty(user) &&
      showFigmaOnboardingForChat &&
      onBoardingFlag &&
      isMSP &&
      !isTrialUser
    ) {
      if (onBoardingPersonalisationHook?.getOnBoardingPersonalisation) {
        onBoardingPersonalisationHook?.getOnBoardingPersonalisation(
          user,
          personalisationCallBack
        );
      }
    }
  }, [user]);

  function handleTabError(error) {
    // eslint-disable-next-line no-unused-expressions
    // window.logger?.info?.('TAB_ERROR_BOUNDARY', {
    //   error: JSON.stringify(error.message),
    //   errorStack: JSON.stringify(error.stack),
    //   userId: user?.userId,
    //   customerId: user?.customerId
    // });
    // window.logger?.flush();

    loggerInfo('TAB_ERROR_BOUNDARY', {
      error: JSON.stringify(error.message),
      errorStack: JSON.stringify(error.stack),
      userId: user?.userId,
      customerId: user?.customerId
    });
  }
  function estimateSizeInBytes(obj) {
    const json = JSON.stringify(obj);
    return new TextEncoder().encode(json).length;
  }

  // Function to estimate the size of the Apollo cache
  function estimateApolloCacheSize(c) {
    const sizeInBytes = estimateSizeInBytes(c);
    const sizeInMB = (sizeInBytes / (1024 * 1024)).toFixed(2);
    // console.log(`Estimated Apollo Cache Size: ${sizeInMB} MB`);
    return `${sizeInMB} MB`;
  }
  if (sessionStorage.getItem('logCache')) {
    const ticketCache = TicketClient.cache.extract();
    const accountCache = AccountClient.cache.extract();
    const rmmCache = RMMClient.cache.extract();
    console.log(
      'cache',
      ticketCache,
      'memory:: ',
      estimateApolloCacheSize(ticketCache)
    );
    const currentMemoryUsage = performance.memory.usedJSHeapSize;
    console.log('current memory usage', currentMemoryUsage);
    console.log('sops:: new log');
    // console.log(
    //   'accountCache',
    //   accountCache,
    //   'memory:: ',
    //   estimateApolloCacheSize(accountCache)
    // );
    // console.log(
    //   'rmmCache',
    //   rmmCache,
    //   'memory:: ',
    //   estimateApolloCacheSize(rmmCache)
    // );
  }

  if (sessionStorage.getItem('clearTicketCache')) {
    TicketClient.clearStore()
      .then(() => {
        console.log('Cache has been cleared.');
      })
      .catch((error) => {
        console.error('Error clearing the store:', error);
      });
  }
  // if (sessionStorage.getItem('clearAccountsCache')) {
  //   AccountClient.clearStore()
  //     .then(() => {
  //       console.log('Cache has been cleared.');
  //     })
  //     .catch((error) => {
  //       console.error('Error clearing the store:', error);
  //     });
  // }
  // if (sessionStorage.getItem('clearRMMCache')) {
  //   RMMClient.clearStore()
  //     .then(() => {
  //       console.log('Cache has been cleared.');
  //     })
  //     .catch((error) => {
  //       console.error('Error clearing the store:', error);
  //     });
  // }

  return (
    <AppProvider>
      <TicketContextProvider
        value={{
          ...actions,
          openTicket: openTab,
          closeTab: closeTabCb,
          activeTab: activeTab,
          openTab: openTab,
          updateTab: updateTab,
          updateTabState: updateTabState,
          resetTabState: resetTabState,
          updateGlobalState: updateGlobalState,
          updateGlobalStateCustom: updateGlobalStateCustom,
          globalState: state.current.globalState,
          closeAndOpenTab: closeAndOpenTab
        }}
      >
        <StyledTabsContainer overlay={props.overlay}>
          <LDDebugger currentUser={appContext?.user} />
          <AppInfoNavigation />
          {showOnboarding && (
            <>
              <OnboardingGuide
                onBoardingPersonalisationHook={onBoardingPersonalisationHook}
              />
            </>
          )}

          <Tabs
            tabs={tabs}
            selected={selected}
            onSelect={handleTabChange}
            customClassName="ticketing-tabs"
            onChange={onTabChange}
            onTabAdd={newTab}
            closeTab={closeTab}
            refreshTab={refreshTab}
            closeAllTabs={closeAllTabs}
          >
            {serviceUnavailable ? (
              <ErrorPage
                imageURL={`url(${envConfig.imageRoot}svg/PageNotFound.svg)`}
                text={
                  <FormattedMessage id="error.page.config.service.unavailable.title" />
                }
                description={
                  <div className="flex col g5">
                    <FormattedMessage id="app.errorCode.503" />
                    <div
                      className="flex g5 pointer"
                      onClick={() => {
                        refreshTab();
                        setServiceUnavailable(false);
                      }}
                    >
                      <FormattedMessage id="common.retry" />
                      <RefreshIcon />
                    </div>
                  </div>
                }
              />
            ) : (
              <ErrorBoundary
                FallbackComponent={ErrorFallback}
                onReset={() => setErrors(false)}
                resetKeys={[errors]}
                onError={handleTabError}
              >
                <div
                  className="full-height position-relative"
                  key={`${lastUpdatedTime}-${tabs[selected].id}`}
                >
                  {notifyRefreshTab && (
                    <div className="refresh-notification fx actr g10">
                      <SVGIcon
                        size="sm"
                        color="var(--sops-white-to-white)"
                        name="Lightning"
                      />
                      <FormattedMessage id="app.common.new.updates" />
                      <div onClick={resetRefreshTab} className="refresh ptr">
                        <FormattedMessage id="app.common.refresh" />
                      </div>
                    </div>
                  )}
                  {isPending ? (
                    <div className="full-height">
                      ...
                      <Loader />
                    </div>
                  ) : null}
                  {!isPending ? getTabSection(tabs[selected]) || '' : ''}
                </div>
              </ErrorBoundary>
            )}
          </Tabs>
          {/* </div> */}
          {showGlobalTicketConversationWarning && (
            <Modal
              activator={<></>}
              open={showGlobalTicketConversationWarning}
              onClose={() => {
                setShowGlobalTicketConversationWarning(false);
              }}
              variant="confirmation"
              title={<FormattedMessage id="app.general.toast.warning" />}
              primaryAction={{
                content: <FormattedMessage id="common.close" />,
                onAction: closeTicketDetailTabAndGlobalTicketConversation,
                destructive: true
              }}
              iconSize="xl"
              secondaryActions={[
                {
                  content: <FormattedMessage id="common.cancel" />,
                  onAction: () => {
                    setShowGlobalTicketConversationWarning(false);
                  }
                }
              ]}
            >
              <TextContainer>
                <FormattedMessage id="ticket.global.conversation.close.warning" />
              </TextContainer>
            </Modal>
          )}
        </StyledTabsContainer>
      </TicketContextProvider>
    </AppProvider>
  );
});

const TabManager = withRouterForwardRef(TabManagerFC);

export default TabManager;
